/**
 * SOLID Review
 * S — Strong. One concern: who this tab is signed in as — a registered account, a guest, or nobody — what each kind
 *     knows, and the returning guest it seats.
 * O — Good. A new fact the server tells about an account is one more method answering a new value.
 * L — N/A — no inheritance hierarchy (private constructor).
 * I — Strong. Three factories, the kind's three questions, the account's fields and its changes, the snapshot.
 * D — Strong. The session schema's types only.
 */
import type { IEntityClientAccount, IEntityClientPlayer } from '../ports/schemas/ClientSession';
import type { IPayloadProfile } from '../ports/schemas/AuthMoves';

/**
 * THE ACCOUNT, AS A VALUE OBJECT (frontend-domain-oo P4, 2026-10-26; handbook §28.271). The session decisions built
 * and edited the account record inline (`decisions/session/auth.ts`, `entry.ts`, `funnel.ts`, `seating.ts`) and
 * tested its kind in seven places. A registered account came from a token or an accepted code, a guest from the
 * stored identity or a created player, nobody from no identity or a sign-out; the credits were carried over each.
 * Every change answers a new value, field for field in the record's order.
 */
export class Account {
  private constructor(private readonly account: IEntityClientAccount) {}

  /** The account the session holds. */
  static of(account: IEntityClientAccount): Account {
    return new Account(account);
  }

  /**
   * A registered account: a token's (its name unknown until `me` answers) or a code accepted. The address is never
   * known here until the profile reads it.
   */
  static registered(playerId: string, displayName: string | null, matchCredits: number): Account {
    const account: IEntityClientAccount = { kind: 'registered', playerId, displayName, email: null, matchCredits };
    return new Account(account);
  }

  /** A guest: the identity this tab stored, or the player the server just created. */
  static guest(playerId: string, displayName: string, matchCredits: number): Account {
    const account: IEntityClientAccount = { kind: 'guest', playerId, displayName, email: null, matchCredits };
    return new Account(account);
  }

  /** Nobody: no token and no stored guest — or signed out, with no credits. */
  static anonymous(matchCredits: number): Account {
    const account: IEntityClientAccount = { kind: 'anonymous', playerId: null, displayName: null, email: null, matchCredits };
    return new Account(account);
  }

  /** Signed in: only such a player has a profile, and is asked for by `me` and the cold rejoin. */
  isRegistered(): boolean {
    return this.account.kind === 'registered';
  }

  isGuest(): boolean {
    return this.account.kind === 'guest';
  }

  /** No identity at all: the funnel offers the guest/login choice. */
  isAnonymous(): boolean {
    return this.account.kind === 'anonymous';
  }

  get playerId(): string | null {
    return this.account.playerId;
  }

  get email(): string | null {
    return this.account.email;
  }

  get matchCredits(): number {
    return this.account.matchCredits;
  }

  /**
   * A returning guest, seated from the identity this tab already holds (useIdentityBootstrap's guest effect): free
   * tier, no credits. None unless both the id and the name are there.
   */
  returningGuest(): IEntityClientPlayer | null {
    const { playerId, displayName } = this.account;
    if (!playerId || !displayName) return null;
    return { playerId, displayName, matchCredits: 0, isAdmin: false };
  }

  /** The credits `me` counted. */
  withCredits(matchCredits: number): Account {
    const account: IEntityClientAccount = { ...this.account, matchCredits };
    return new Account(account);
  }

  /** The name, address and credits as the server has them, read when the profile opens. */
  withProfile(profile: IPayloadProfile): Account {
    const account: IEntityClientAccount = { ...this.account, displayName: profile.displayName, email: profile.email, matchCredits: profile.matchCredits };
    return new Account(account);
  }

  renamed(displayName: string): Account {
    const account: IEntityClientAccount = { ...this.account, displayName };
    return new Account(account);
  }

  withEmail(email: string | null): Account {
    const account: IEntityClientAccount = { ...this.account, email };
    return new Account(account);
  }

  /** A guest re-identified by the server as one of its own seats: that seat's id, under the name this tab knows. */
  adoptedAs(playerId: string, displayName: string): Account {
    const account: IEntityClientAccount = { ...this.account, playerId, displayName };
    return new Account(account);
  }

  toSnapshot(): IEntityClientAccount {
    return this.account;
  }
}
